import React, { useContext, useCallback } from "react";
import { observer } from "mobx-react";
import { ActionButtons } from "src/components/header/actionButtons/ActionButtons";
import { AppbarButtons } from "src/components/header/appbarButtons/AppbarButtons";
import { Backnavigation } from "src/components/header/backnavigation/Backnavigation";
import { Searchbar } from "src/components/header/searchbar/Searchbar";
import { Close } from "src/components/header/close/Close";
import { RootStoreContext } from "src/stores/RootStore";
import { Route } from "src/config/routes";
import { AppbarButtonProps } from "src/components/header/appbarButtons/AppbarButtons";
import { Helmet } from "react-helmet";
import styles from "./Appheader.module.css";
import { UI } from "@wwimmo/ui";
import { useSidebarFunctions } from "src/hooks/sidebar-functions/useSidebarFunctions";
import { Role } from "src/network/User";

const iconSize = 42;

const CornerBase = (style: { left?: number; right?: number; transform?: string }) => {
    const { uiStore } = useContext(RootStoreContext);
    return (
        <svg
            className={styles.Corner}
            style={style}
            width={`${uiStore.isMobile ? 20 : 40}px`}
            height={`${uiStore.isMobile ? 20 : 40}px`}
            viewBox="0 0 20 20"
        >
            <path
                d="M0,0 L40,0 L40,40 L0,40 L0,0 Z M20,40 C31.045695,40 40,31.045695 40,20 C40,8.954305 31.045695,0 20,0 C8.954305,0 0,8.954305 0,20 C0,31.045695 8.954305,40 20,40 Z"
                fill="var(--primary)"
            />
        </svg>
    );
};
const Corner = observer(CornerBase);

const AppHeaderLeftBase = () => {
    const { uiStore, authStore, uiSidebarStore } = useContext(RootStoreContext);
    const { expandSidebar } = useSidebarFunctions();

    const onClickHamburgerMenu = useCallback(() => {
        expandSidebar();
    }, [expandSidebar]);

    // When the sidebar logo overflows its container (e.g. sidebar collapsed to 50px but
    // logo extends further right), push the back navigation past the logo's right edge.
    // logoRightEdge is measured from the actual rendered image size (set via onLoad in
    // SidebarLogo), so a small logo produces a small — or zero — extra padding.
    const backnavPaddingLeft =
        !uiStore.isMobile && uiSidebarStore.isSidebarActive && uiSidebarStore.logoRightEdge > 0
            ? Math.max(0, uiSidebarStore.logoRightEdge - uiSidebarStore.sidebarWidth)
            : 0;

    if (!uiStore.showSearchbar) {
        return (
            <UI.Col
                className="AppHeaderLeft"
                xs={9}
                sm={uiStore.showSearchbar ? 6 : 8}
                lg={uiStore.showSearchbar ? 4 : 3}
            >
                <UI.Row>
                    {authStore.user?.role === Role.MANAGER || authStore.user?.role === Role.ECADMIN ? (
                        <UI.Col xs={2} sm={0} className="d-md-none">
                            <UI.Icon icon={UI.SVGIcon.List} onClick={onClickHamburgerMenu} />
                        </UI.Col>
                    ) : undefined}

                    <UI.Col xs={10} sm={12}>
                        <div style={{ paddingLeft: backnavPaddingLeft, transition: "padding-left 0.3s" }}>
                            <Backnavigation />
                        </div>
                    </UI.Col>
                </UI.Row>
            </UI.Col>
        );
    }
    return null;
};
const AppHeaderLeft = observer(AppHeaderLeftBase);

const AppHeaderMiddleBase = () => {
    const { uiStore, authStore, navStore } = useContext(RootStoreContext);
    if (uiStore.showSearchbar) {
        return null;
    }

    return (
        <UI.Col
            className="AppHeaderMiddle pt-1 text-left text-md-center d-sm-none d-lg-block"
            xs={{ span: 12, order: 3, offset: 0 }}
            md={{ span: uiStore.showSearchbar ? 4 : 6, order: 2, offset: uiStore.showSearchbar ? 4 : 0 }}
        >
            <div className={styles.Titlebar}>
                <h1
                    style={
                        uiStore.isMobile
                            ? { display: "block", paddingRight: iconSize * 3 }
                            : { display: "inline", paddingRight: 0 }
                    }
                >
                    <span className="ellipsise">{navStore.appheaderTitle}</span>
                    {authStore.isLoggedIn && <ActionButtons iconSize={iconSize} isMobile={uiStore.isMobile} />}
                </h1>
            </div>
            {navStore.currentSubtitle ? <h2>{navStore.currentSubtitle}</h2> : <React.Fragment />}
        </UI.Col>
    );
};
const AppHeaderMiddle = observer(AppHeaderMiddleBase);

const AppHeaderRightBase = () => {
    const { uiStore, authStore } = useContext(RootStoreContext);
    const { showSearchbar, setSearchbar } = uiStore;

    const displaySearchInput = useCallback(() => {
        setSearchbar(true);
    }, [setSearchbar]);

    const hideSearchInput = useCallback(() => {
        setSearchbar(false);
    }, [setSearchbar]);

    const appbarButtons: AppbarButtonProps[] = [
        { svgIcon: UI.SVGIcon.Search, onClick: displaySearchInput, isDisplayed: uiStore.showSearchAppbarButton },
        { svgIcon: UI.SVGIcon.Profile, to: Route.profile, isDisplayed: uiStore.showProfileAppbarButton }
    ];

    return (
        <UI.Col
            className="AppHeaderRight d-flex flex-nowrap justify-content-end"
            xs={{ span: showSearchbar ? 12 : 3, order: 2 }}
            sm={{ span: showSearchbar ? 8 : 4, order: 3, offset: showSearchbar ? 2 : 0 }}
            lg={{ span: showSearchbar ? 4 : 3, order: 3, offset: showSearchbar ? 4 : 0 }}
        >
            {authStore.isLoggedIn && (
                <React.Fragment>
                    {showSearchbar ? (
                        <Searchbar onClose={hideSearchInput} />
                    ) : (
                        <AppbarButtons buttons={appbarButtons} />
                    )}
                </React.Fragment>
            )}
        </UI.Col>
    );
};
const AppHeaderRight = observer(AppHeaderRightBase);

const AppheaderBase = () => {
    const { uiStore, navStore } = useContext(RootStoreContext);
    const { showSearchbar, setSearchbar } = uiStore;

    const closeSearchbar = useCallback(
        (e: any) => {
            if (showSearchbar && e.target.id !== "searchbarInput") {
                setSearchbar(false);
            }
        },
        [showSearchbar, setSearchbar]
    );

    return (
        <div className={`${styles.Appheader} ${uiStore.isHeaderDisplayed ? "" : "d-none"}`} onClick={closeSearchbar}>
            <Helmet>
                <title>{navStore.currentTitle}</title>
            </Helmet>
            <UI.Container
                className={styles.Container}
                style={{
                    height: uiStore.headerHeight,
                    paddingTop: 20
                }}
                fluid
            >
                {uiStore.closeAppbar ? (
                    <UI.Row>
                        <UI.Col xs={{ span: 8 }} md={{ span: 3, order: 1 }}>
                            <Backnavigation />
                        </UI.Col>
                        <UI.Col
                            xs={{ span: 12, order: 3 }}
                            sm={{ span: 6, order: 2 }}
                            className="pt-1 text-left text-md-center"
                        >
                            <div className={styles.Titlebar}>
                                <h1>{navStore.appheaderTitle}</h1>
                            </div>
                        </UI.Col>
                        <UI.Col xs={{ span: 4, order: 2 }} md={{ span: 3, order: 3 }} className="text-right">
                            <Close />
                        </UI.Col>
                    </UI.Row>
                ) : (
                    <UI.Row className="align-items-center">
                        <AppHeaderLeft />
                        <AppHeaderMiddle />
                        <AppHeaderRight />
                    </UI.Row>
                )}
            </UI.Container>
            <div className={styles.CornerContainer}>
                <Corner left={0} />
                <Corner right={0} transform="scaleX(-1)" />
            </div>
        </div>
    );
};
export const Appheader = observer(AppheaderBase);
